import { AfterViewInit, Component, ViewChild, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  UntypedFormArray,
  Validators,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTable,
  MatTableDataSource,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { MessageType } from '@constants/message/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ItemPrepGroupDialogComponent } from '@components/item-prep-group-dialog/1.0/item-prep-group-dialog.component';
import { NgIf } from '@angular/common';
import { MessageComponent } from '../../message/1.0/message.component';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TxpIntegersOnlyDirective } from '../../../directives/integers-only/1.0/txp-integers-only.directive';
import { ButtonComponent } from '../../button/1.0/button.component';

@Component({
  selector: 'txp-advanced-notice-template-form',
  templateUrl: './advanced-notice-template-form.component.html',
  styleUrls: ['./advanced-notice-template-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdvancedNoticeTemplateFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AdvancedNoticeTemplateFormComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    MessageComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    TxpIntegersOnlyDirective,
    MatError,
    ButtonComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
  ],
})
export class AdvancedNoticeTemplateFormComponent
  implements AfterViewInit, ControlValueAccessor, Validator
{
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatTable) table: MatTable<any>;

  advancedNoticeReadOnly = false;
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize;
  messageType = MessageType;
  dataSource = new MatTableDataSource<any>([]);
  displayColumns = ['name', 'advancedNoticeHours', 'actions'];
  form: UntypedFormGroup;
  formArray: UntypedFormArray = new UntypedFormArray([]);
  isLoading = false;
  disabled: boolean;
  onChangeFn: any = () => undefined;
  onTouched: any = () => undefined;
  defaultFormValue: any;

  private unsubscribe = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
  ) {
    this.form = this.formBuilder.group({
      advancedNoticeItemGroups: this.formBuilder.array([]),
    });
    this.defaultFormValue = this.form.value;
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item: FormGroup, property: string) => {
      return item.get(property).value;
    };
  }

  writeValue(array): void {
    const controls: UntypedFormGroup[] = [];
    if (Array.isArray(array) && array.length) {
      for (let i = 0, iLen = array.length; i < iLen; i++) {
        let advancedNoticeItem = array[i];
        controls.push(this.createRow(advancedNoticeItem));
      }
    }
    this.formArray = new UntypedFormArray(controls);
    this.setData();
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      // extract extra layer due to form array
      fn(data?.advancedNoticeItemGroups);
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.form.valid
      ? undefined
      : {
          invalidForm: {
            valid: false,
            message: this.translateService.instant('form.error.fieldsAreInvalid'),
          },
        };
  }

  createRow(row) {
    return this.formBuilder.group({
      name: row.name,
      advancedNoticeHours: this.formBuilder.control(
        { value: row?.advancedNoticeHours || '', disabled: this.advancedNoticeReadOnly },
        [Validators.required, Validators.min(1)],
      ),
      items: this.formBuilder.array(row.items || []),
    });
  }

  handleDelete(name: string, formArray: UntypedFormArray): void {
    formArray.removeAt(formArray.value.findIndex((item) => item.name === name));
    this.formArray = formArray;
    this.setData();
    this.form.markAsDirty();
  }

  handleEdit(item) {
    this.openDialog(item);
  }

  addItem() {
    this.openDialog();
  }

  openDialog(item?) {
    const matDialogRef = this.dialog.open(ItemPrepGroupDialogComponent, {
      data: {
        ...item,
        translations: {
          title: this.translateService.instant('store.dynamicPrepTime.prepGroupModal.title'),
          type: this.translateService.instant('catalog.productGroup.products'),
          group: this.translateService.instant(
            'store.dynamicPrepTime.prepGroupModal.advancedPrepGroupName',
          ),
          listTitle: this.translateService.instant('catalog.nav.products'),
        },
      },
    });

    matDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const { name, items, previousValue } = result;
        const newRowData = {
          name: name,
          advancedNoticeHours: 1,
          items: (items || []).map((catalogItem) => {
            return { name: catalogItem.name, sku: catalogItem.catalogEntryId };
          }),
        };
        const formArray = this.form.get('advancedNoticeItemGroups') as UntypedFormArray;
        if (previousValue && previousValue.advancedNoticeHours) {
          let index = formArray.value.findIndex((item) => item.name === previousValue.name);
          formArray.removeAt(index);
          formArray.push(
            this.createRow({
              name: newRowData.name,
              items: newRowData.items,
              advancedNoticeHours:
                previousValue.advancedNoticeHours || newRowData.advancedNoticeHours,
            }),
          );
        } else {
          formArray.push(this.createRow(newRowData));
        }
        this.formArray = formArray;
        this.setData();
      }
    });
  }

  setData() {
    this.form.setControl('advancedNoticeItemGroups', this.formArray);
    this.dataSource.data = this.formArray.controls;
  }
}
